/**
 * Maps drug API response to a unified format for the DrugSearchNew component
 * This mapper transforms the PrescriptSure API response structure to match
 * the component's expected data format
 */

/**
 * Map API drug info response to unified drug data structure
 * @param {Object} apiResponse - The API response from drugsApiService.getDrugInfo()
 * @returns {Object|null} - Mapped drug data or null if invalid
 */
export function mapDrugInfoResponse(apiResponse) {
  if (!apiResponse || !apiResponse.Basic_Drug_Info) {
    console.error('❌ Invalid API response: Missing Basic_Drug_Info');
    return null;
  }

  const basicInfo = apiResponse.Basic_Drug_Info;
  const patientEd = apiResponse.Patient_Education_Info;
  
  // Extract description from patient education "Uses" section
  let description = '';
  if (patientEd?.Sections) {
    const usesSection = patientEd.Sections.find(s => s.Title === 'Uses');
    description = usesSection?.Text || '';
  }

  const mapped = {
    // Basic identification
    id: basicInfo.DispensableDrugID?.toString() || '',
    name: basicInfo.DispensableGenericDesc || '',
    
    // Drug details
    genericName: basicInfo.DispensableGenericDesc || '',
    type: basicInfo.NameTypeCodeDesc || 'Generic',
    status: basicInfo.StatusCodeDesc || 'Active',
    federalLegend: basicInfo.FederalLegendCodeDesc || 'Prescription Required',
    description: description,
    
    // Clinical details
    classification: basicInfo.DefaultETCDesc || '',
    route: basicInfo.RouteDesc || '',
    dosageForm: basicInfo.DoseFormDesc || '',
    strength: basicInfo.MedStrength 
      ? `${basicInfo.MedStrength} ${basicInfo.MedStrengthUnit || ''}`.trim()
      : '',
    
    // Indications
    indications: mapIndications(apiResponse.Indication_Links),
    
    // Precautions (pregnancy, lactation, pediatric, geriatric, label warnings)
    precautions: mapPrecautions(apiResponse),
    
    // Side effects
    sideEffects: mapSideEffects(apiResponse.Side_Effects),
    
    // Interactions (drug-drug, drug-food only)
    interactions: mapInteractions(apiResponse),
    
    // Drug-Disease Contraindications (separate from interactions)
    drugDiseaseContraindications: mapDrugDiseaseContraindications(apiResponse),
    
    // Therapeutic alternatives
    alternatives: mapAlternatives(apiResponse.Therapeutic_Alternatives),
    
    // Ingredients
    ingredients: mapIngredients(basicInfo.Ingredients),
    
    // Most common brands
    brands: apiResponse.Most_Common_Brands?.Items || [],
    
    // Patient education (preserve original for rendering)
    patientEducation: patientEd,
  };

  console.log('✅ Successfully mapped drug info:', {
    name: mapped.name,
    indicationsCount: mapped.indications.length,
    precautionsCount: mapped.precautions.length,
    sideEffectsCount: mapped.sideEffects.length,
    interactionsCount: mapped.interactions.length,
    alternativesCount: mapped.alternatives.length,
    ingredientsCount: mapped.ingredients.length,
  });

  return mapped;
}

/**
 * Map indications to array of objects with condition and approval status
 */
function mapIndications(indicationsData) {
  if (!indicationsData?.Indications || !Array.isArray(indicationsData.Indications)) {
    return [];
  }
  
  return indicationsData.Indications.map(ind => ({
    condition: ind.ConditionDesc || '',
    approved: ind.IndicationLabeledCodeDesc === 'Drug indication has been approved by the FDA',
    status: ind.IndicationLabeledCodeDesc || '',
  }))
  .filter(ind => ind.condition)
  .sort((a, b) => {
    // FDA approved first
    if (a.approved && !b.approved) return -1;
    if (!a.approved && b.approved) return 1;
    return a.condition.localeCompare(b.condition);
  });
}

/**
 * Map precautions from various sources (pregnancy, lactation, pediatric, geriatric, label warnings)
 */
function mapPrecautions(apiResponse) {
  const precautions = [];
  
  // Pregnancy precautions
  if (apiResponse.Pregnancy_Precautions?.Pregnancy_Precautions) {
    apiResponse.Pregnancy_Precautions.Pregnancy_Precautions.forEach(prec => {
      precautions.push({
        group: 'Pregnancy',
        severity: prec.SeverityDesc || 'Monitoring',
        text: prec.Narrative || prec.PrecautionsPregnancyDesc || '',
        monographId: prec.MonographID,
      });
    });
  }
  
  // Lactation precautions
  if (apiResponse.Lactation_Precautions?.Lactation_Precautions) {
    apiResponse.Lactation_Precautions.Lactation_Precautions.forEach(prec => {
      precautions.push({
        group: 'Lactation',
        severity: prec.SeverityDesc || 'Monitoring',
        text: prec.Narrative || prec.PrecautionsLactationDesc || '',
      });
    });
  }
  
  // Pediatric precautions
  if (apiResponse.Pediatric_Precautions?.Pediatric_Precautions) {
    apiResponse.Pediatric_Precautions.Pediatric_Precautions.forEach(prec => {
      const ageRange = [];
      if (prec.AgeLowDesc) ageRange.push(prec.AgeLowDesc);
      if (prec.AgeHighDesc) ageRange.push(prec.AgeHighDesc);
      
      precautions.push({
        group: 'Pediatric',
        severity: prec.SeverityDesc || 'Monitoring',
        text: prec.Narrative || prec.PrecautionsPediatricDesc || '',
        ageRange: ageRange.length > 0 ? ageRange.join(' - ') : undefined,
      });
    });
  }
  
  // Geriatric precautions
  if (apiResponse.Geriatric_Precautions?.Geriatric_Precautions) {
    apiResponse.Geriatric_Precautions.Geriatric_Precautions.forEach(prec => {
      precautions.push({
        group: 'Geriatric',
        severity: prec.SeverityDesc || 'Monitoring',
        text: prec.Narrative || prec.PrecautionsGeriatricDesc || '',
        drugName: prec.PrecautionsGeriatricDesc || '',
        hasHepaticRisk: prec.HasHepaticRisk || false,
        hasRenalRisk: prec.HasRenalRisk || false,
      });
    });
  }
  
  // Label warnings
  if (apiResponse.Label_Warning_info?.Label_warnings) {
    apiResponse.Label_Warning_info.Label_warnings.forEach(warning => {
      precautions.push({
        group: 'Label Warning',
        severity: 'Warning',
        text: warning.LabelWarningText || '',
        priority: warning.Priority,
      });
    });
  }
  
  // Drug-Disease Contraindications as precautions
  if (apiResponse.Drug_Disease_Contraindication_list?.DrugDiseaseContraindications) {
    apiResponse.Drug_Disease_Contraindication_list.DrugDiseaseContraindications.forEach(contra => {
      precautions.push({
        group: 'Drug-Disease',
        severity: contra.SeverityDesc || 'Moderate Warning',
        text: `Caution with ${contra.ConditionDesc}`,
      });
    });
  }
  
  return precautions;
}

/**
 * Normalize frequency from API format to filter format
 */
function normalizeFrequency(apiFrequency) {
  if (!apiFrequency) return 'rare/very rare';
  
  const freqLower = apiFrequency.toLowerCase();
  if (freqLower.includes('more frequent')) {
    return 'more frequent';
  } else if (freqLower.includes('less frequent')) {
    return 'less frequent';
  } else if (freqLower.includes('rare') || freqLower.includes('very rare')) {
    return 'rare/very rare';
  }
  return 'rare/very rare';
}

/**
 * Normalize severity from API format to filter format
 */
function normalizeSeverity(apiSeverity) {
  if (!apiSeverity) return 'Less Severe';
  
  const sevLower = apiSeverity.toLowerCase();
  if (sevLower.includes('severe') && !sevLower.includes('less')) {
    return 'Severe';
  }
  return 'Less Severe';
}

/**
 * Map side effects with frequency and severity
 */
function mapSideEffects(sideEffectsData) {
  if (!sideEffectsData?.Side_Effects || !Array.isArray(sideEffectsData.Side_Effects)) {
    return [];
  }
  
  return sideEffectsData.Side_Effects.map(effect => ({
    name: effect.ConditionDesc || '',
    frequency: normalizeFrequency(effect.FrequencyOfOccurrenceCodeDesc),
    severity: normalizeSeverity(effect.SeverityDesc),
    severityLevel: effect.Severity !== undefined ? parseInt(effect.Severity) : 0,
    visibility: effect.VisibilityCodeDesc || '',
    isHypersensitivity: effect.HypersensitivityIndicator || false,
  })).filter(effect => effect.name);
}

/**
 * Map frequency descriptions to standardized format
 */
function mapFrequency(freq) {
  if (!freq) return 'rare/very rare';
  
  const freqLower = freq.toLowerCase();
  if (freqLower.includes('common') || freqLower.includes('frequent')) {
    return 'more frequent';
  } else if (freqLower.includes('occasional') || freqLower.includes('less')) {
    return 'less frequent';
  }
  return 'rare/very rare';
}

/**
 * Map interactions (drug-drug and drug-food only)
 */
function mapInteractions(apiResponse) {
  const interactions = [];
  
  // Drug-drug interactions
  if (apiResponse.Drug_Drug_Interactions_list?.Interactions) {
    apiResponse.Drug_Drug_Interactions_list.Interactions.forEach(interaction => {
      interactions.push({
        type: 'Drug',
        with: interaction.InteractionDesc || '',
        clinicalEffect: interaction.ClinicalEffect1Desc || '',
        clinicalEffectCode: interaction.ClinicalEffect1 || '',
        severity: interaction.SeverityDesc || 'Minor',
        severityLevel: interaction.Severity,
        monographId: parseInt(interaction.MonographID) || 0,
        monographType: 'drugDrug',
      });
    });
  }
  
  // Drug-food interactions
  if (apiResponse.Drug_Food_Interactions?.result) {
    apiResponse.Drug_Food_Interactions.result.forEach(interaction => {
      interactions.push({
        type: 'Food',
        with: interaction.AdviceMessage || '',
        clinicalEffect: interaction.ClinicalResult || '',
        severity: interaction.SignificanceDesc || 'Minor',
        severityLevel: interaction.Significance,
        monographId: parseInt(interaction.MonographID) || 0,
        monographType: 'drugFood',
      });
    });
  }
  
  return interactions;
}

/**
 * Map drug-disease contraindications (separate from interactions)
 */
function mapDrugDiseaseContraindications(apiResponse) {
  const contraindications = [];
  
  if (apiResponse.Drug_Disease_Contraindication_list?.DrugDiseaseContraindications) {
    apiResponse.Drug_Disease_Contraindication_list.DrugDiseaseContraindications.forEach(contra => {
      contraindications.push({
        condition: contra.ConditionDesc || '',
        severity: contra.SeverityDesc || 'Moderate Warning',
        severityLevel: contra.Severity,
      });
    });
  }
  
  return contraindications;
}

/**
 * Map therapeutic alternatives
 */
function mapAlternatives(alternativesData) {
  if (!alternativesData?.Items || !Array.isArray(alternativesData.Items)) {
    return [];
  }
  
  return alternativesData.Items.map(alt => ({
    name: alt.GENERIC_DRUG_NAME || '',
    subGroup: alt.SUB_GROUP_DESC || undefined,
    genericId: alt.DISPENSABLE_GENERIC_ID,
  })).filter(alt => alt.name);
}

/**
 * Map ingredients (active and inactive)
 */
function mapIngredients(ingredientsData) {
  if (!ingredientsData || !Array.isArray(ingredientsData)) {
    return [];
  }
  
  return ingredientsData.map(ing => ({
    name: ing.IngredientDesc || '',
    id: ing.IngredientID,
    type: ing.IngredientType || 'Unknown', // 'Active' or 'Inactive'
  })).filter(ing => ing.name);
}

/**
 * Map drug search results to autocomplete options
 * @param {Array} searchResults - Array of drug search results
 * @returns {Array} - Array of autocomplete options
 */
export function mapDrugSearchResults(searchResults) {
  if (!searchResults || !Array.isArray(searchResults)) {
    return [];
  }
  
  return searchResults.map(drug => ({
    value: drug.DispensableDrugID?.toString() || '',
    label: drug.DispensableDrugDesc || '',
    subtitle: drug.DispensableGenericDesc 
      ? `Generic: ${drug.DispensableGenericDesc}`
      : undefined,
  }));
}
